import { set, upd } from "../../common/util/Immutable";
import { createFracList } from "../util/FracList";

export type TPoint = [number, number];

export type TAppNode = {
  type: "app";

  children: {
    surfaces: { [id: TNodeID<"surface">]: true };
    states: { [id: TNodeID<"state">]: true };

    events: {
      app_open?: TNodeID<"signal_with_data">;
      app_focus?: TNodeID<"signal_with_data">;
      app_unfocus?: TNodeID<"signal_with_data">;
      app_hide?: TNodeID<"signal_with_data">;
      app_unhide?: TNodeID<"signal_with_data">;
      app_freeze?: TNodeID<"signal_with_data">;
      app_unfreeze?: TNodeID<"signal_with_data">;
      app_stop?: TNodeID<"signal_with_data">;
    };
  };

  parent: never;
};

export type TSurfaceNode = {
  type: "surface";

  children: {
    nodes: (
      | TNodeID<"div">
      | TNodeID<"text">
      | TNodeID<"repeater">
      | TNodeID<"branch">
      | TNodeID<"branch_group">
    )[];
    states: { [id: TNodeID<"state">]: true };

    events: {
      click: TNodeID<"signal_with_data">;
      pointer_down: TNodeID<"signal_with_data">;
      pointer_up: TNodeID<"signal_with_data">;
    };

    props: { flex_direction: TNodeID<"enum_data"> };
  };

  props: {
    flex_direction: "row" | "column" | "row-reverse" | "column-reverse";
  };
  parent: { id: TNodeID<"app">; key: "surfaces" };
};

export type TDivNode = {
  type: "div";

  children: {
    nodes: (
      | TNodeID<"div">
      | TNodeID<"text">
      | TNodeID<"repeater">
      | TNodeID<"branch">
      | TNodeID<"branch_group">
    )[];
    states: { [id: TNodeID<"state">]: true };

    events: {
      click: TNodeID<"signal_with_data">;
      pointer_down: TNodeID<"signal_with_data">;
      pointer_up: TNodeID<"signal_with_data">;
    };

    props: { flex_direction: TNodeID<"enum_data"> };
  };

  props: {
    flex_direction: "row" | "column" | "row-reverse" | "column-reverse";
  };
  parent:
    | { id: TNodeID<"surface">; key: "nodes" }
    | { id: TNodeID<"div">; key: "nodes" }
    | { id: TNodeID<"repeater">; key: "nodes" }
    | { id: TNodeID<"branch">; key: "nodes" };
};

export type TTextNode = {
  type: "text";

  children: {
    props: {
      flex_grow: TNodeID<"bool_data">;
      flex_shrink: TNodeID<"bool_data">;
      text: TNodeID<"string_data">;
    };
  };

  props: { flex_grow: boolean; flex_shrink: boolean; text: string };
  parent:
    | { id: TNodeID<"surface">; key: "nodes" }
    | { id: TNodeID<"div">; key: "nodes" }
    | { id: TNodeID<"repeater">; key: "nodes" }
    | { id: TNodeID<"branch">; key: "nodes" };
};

export type TRepeaterNode = {
  type: "repeater";

  children: {
    nodes: (
      | TNodeID<"div">
      | TNodeID<"text">
      | TNodeID<"repeater">
      | TNodeID<"branch">
      | TNodeID<"branch_group">
    )[];
    input: TNodeID<"repeater_input">;
  };

  parent:
    | { id: TNodeID<"surface">; key: "nodes" }
    | { id: TNodeID<"div">; key: "nodes" }
    | { id: TNodeID<"repeater">; key: "nodes" }
    | { id: TNodeID<"branch">; key: "nodes" };
};

export type TBranchNode = {
  type: "branch";

  children: {
    nodes: (
      | TNodeID<"div">
      | TNodeID<"text">
      | TNodeID<"repeater">
      | TNodeID<"branch">
      | TNodeID<"branch_group">
    )[];
    condition: TNodeID<"bool_data">;
  };

  parent:
    | { id: TNodeID<"surface">; key: "nodes" }
    | { id: TNodeID<"div">; key: "nodes" }
    | { id: TNodeID<"repeater">; key: "nodes" }
    | { id: TNodeID<"branch">; key: "nodes" }
    | { id: TNodeID<"branch_group">; key: "nodes" };
};

export type TBranchGroupNode = {
  type: "branch_group";

  children: {
    nodes: TNodeID<"branch">[];
  };

  parent:
    | { id: TNodeID<"surface">; key: "nodes" }
    | { id: TNodeID<"div">; key: "nodes" }
    | { id: TNodeID<"repeater">; key: "nodes" }
    | { id: TNodeID<"branch">; key: "nodes" };
};

export type TNumberDataNode = {
  type: "number_data";

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_fill">; key: "count" }
    | { id: TNodeID<"list_index_value">; key: "index" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_index" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"repeater_input">; key: "index" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TStringDataNode = {
  type: "string_data";

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "key" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_key" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" }
    | { id: TNodeID<"network">; key: "response" };
};

export type TBoolDataNode = {
  type: "bool_data";

  parent:
    | { id: TNodeID<"branch">; key: "condition" }
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TEnumDataNode = {
  type: "enum_data";

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TStreamDataNode = {
  type: "stream_data";

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" }
    | { id: TNodeID<"network">; key: "response" };
};

export type TUnionDataNode = {
  type: "union_data";

  children: {
    options: (
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">
    )[];
  };

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TStructDataNode = {
  type: "struct_data";

  children: {
    fields: (
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">
    )[];
  };

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" }
    | { id: TNodeID<"network">; key: "response" };
};

export type TListDataNode = {
  type: "list_data";

  children: {
    ops: (
      | TNodeID<"list_elem">
      | TNodeID<"list_slice">
      | TNodeID<"list_fill">
      | TNodeID<"list_index_value">
      | TNodeID<"list_find">
    )[];
    rest?: TNodeID<"list_data">;
  };

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_data">; key: "rest" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"signal_foreach">; key: "input" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TMapDataNode = {
  type: "map_data";

  children: {
    ops: (
      | TNodeID<"map_key_value">
      | TNodeID<"map_slice">
      | TNodeID<"map_find">
    )[];
    rest?: TNodeID<"map_data">;
  };

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"map_data">; key: "rest" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TListElemNode = {
  type: "list_elem";

  parent: { id: TNodeID<"list_data">; key: "ops" };
};

export type TListSliceNode = {
  type: "list_slice";

  parent: { id: TNodeID<"list_data">; key: "ops" };
};

export type TListFillNode = {
  type: "list_fill";

  children: {
    count: TNodeID<"number_data">;
  };

  parent: { id: TNodeID<"list_data">; key: "ops" };
};

export type TListIndexValueNode = {
  type: "list_index_value";

  children: {
    index: TNodeID<"number_data">;
    value:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
  };

  parent: { id: TNodeID<"list_data">; key: "ops" };
};

export type TListFindNode = {
  type: "list_find";

  children: {
    condition: TNodeID<"function">;
    match_index: TNodeID<"number_data">;
    match_value:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
  };

  parent: { id: TNodeID<"list_data">; key: "ops" };
};

export type TMapKeyValueNode = {
  type: "map_key_value";

  children: {
    key: TNodeID<"string_data">;
    value:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
  };

  parent: { id: TNodeID<"map_data">; key: "ops" };
};

export type TMapSliceNode = {
  type: "map_slice";

  parent: { id: TNodeID<"map_data">; key: "ops" };
};

export type TMapFindNode = {
  type: "map_find";

  children: {
    condition: TNodeID<"function">;
    match_key: TNodeID<"string_data">;
    match_value:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
  };

  parent: { id: TNodeID<"map_data">; key: "ops" };
};

export type TUnaryOpNode = {
  type: "unary_op";

  props: { op: "++" | "--" | "!" };
  parent: { id: TNodeID<"function">; key: "body" };
};

export type TNAryUnorderedOpNode = {
  type: "n_ary_unordered_op";

  props: { op: "+" | "*" | "&&" | "||" };
  parent: { id: TNodeID<"function">; key: "body" };
};

export type TNAryOrderedOpNode = {
  type: "n_ary_ordered_op";

  props: { op: "-" | "/" };
  parent: { id: TNodeID<"function">; key: "body" };
};

export type TFunctionNode = {
  type: "function";

  children: {
    inputs: (
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">
    )[];
    outputs: (
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">
    )[];
    body: {
      [
        id:
          | TNodeID<"number_data">
          | TNodeID<"string_data">
          | TNodeID<"bool_data">
          | TNodeID<"enum_data">
          | TNodeID<"stream_data">
          | TNodeID<"union_data">
          | TNodeID<"struct_data">
          | TNodeID<"list_data">
          | TNodeID<"map_data">
          | TNodeID<"function">
          | TNodeID<"unary_op">
          | TNodeID<"n_ary_unordered_op">
          | TNodeID<"n_ary_ordered_op">
      ]: true;
    };
  };

  parent:
    | { id: TNodeID<"union_data">; key: "options" }
    | { id: TNodeID<"struct_data">; key: "fields" }
    | { id: TNodeID<"list_index_value">; key: "value" }
    | { id: TNodeID<"list_find">; key: "condition" }
    | { id: TNodeID<"list_find">; key: "match_value" }
    | { id: TNodeID<"map_key_value">; key: "value" }
    | { id: TNodeID<"map_find">; key: "condition" }
    | { id: TNodeID<"map_find">; key: "match_value" }
    | { id: TNodeID<"function">; key: "inputs" }
    | { id: TNodeID<"function">; key: "outputs" }
    | { id: TNodeID<"function">; key: "body" }
    | { id: TNodeID<"repeater_input">; key: "value" }
    | { id: TNodeID<"state">; key: "output" }
    | { id: TNodeID<"signal_with_data">; key: "data" }
    | { id: TNodeID<"signal_merger">; key: "output_data" }
    | { id: TNodeID<"network">; key: "request" };
};

export type TRepeaterInputNode = {
  type: "repeater_input";

  children: {
    value:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
    index?: TNodeID<"number_data">;
  };

  parent: { id: TNodeID<"repeater">; key: "input" };
};

export type TStateNode = {
  type: "state";

  children: {
    updaters: TNodeID<"signal_with_data">[];
    output:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
  };

  parent:
    | { id: TNodeID<"app">; key: "states" }
    | { id: TNodeID<"surface">; key: "states" }
    | { id: TNodeID<"div">; key: "states" };
};

export type TSignalNode = {
  type: "signal";

  parent: { id: TNodeID<"network_socket">; key: "close" };
};

export type TSignalWithDataNode = {
  type: "signal_with_data";

  children: {
    data:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
  };

  parent:
    | { id: TNodeID<"state">; key: "updaters" }
    | { id: TNodeID<"signal_merger">; key: "inputs" }
    | { id: TNodeID<"signal_splitter">; key: "outputs" }
    | { id: TNodeID<"signal_foreach">; key: "output" }
    | { id: TNodeID<"network_socket">; key: "send" }
    | { id: TNodeID<"network_socket">; key: "on_receive" };
};

export type TSignalMergerNode = {
  type: "signal_merger";

  children: {
    output_data:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
    inputs: TNodeID<"signal_with_data">[];
  };

  parent: never;
};

export type TSignalSplitterNode = {
  type: "signal_splitter";

  children: {
    outputs: TNodeID<"signal_with_data">[];
  };

  parent: never;
};

export type TSignalForeachNode = {
  type: "signal_foreach";

  children: {
    input: TNodeID<"list_data">;
    output: TNodeID<"signal_with_data">;
  };

  parent: never;
};

export type TNetworkNode = {
  type: "network";

  children: {
    request?:
      | TNodeID<"number_data">
      | TNodeID<"string_data">
      | TNodeID<"bool_data">
      | TNodeID<"enum_data">
      | TNodeID<"stream_data">
      | TNodeID<"union_data">
      | TNodeID<"struct_data">
      | TNodeID<"list_data">
      | TNodeID<"map_data">
      | TNodeID<"function">;
    response?:
      | TNodeID<"string_data">
      | TNodeID<"stream_data">
      | TNodeID<"struct_data">;
  };

  parent: never;
};

export type TNetworkSocketNode = {
  type: "network_socket";

  children: {
    close: TNodeID<"signal">;
    send: TNodeID<"signal_with_data">;
    on_receive: TNodeID<"signal_with_data">;
  };

  parent: never;
};

createFracList();

export type TNodeUnion =
  | TAppNode
  | TSurfaceNode
  | TDivNode
  | TTextNode
  | TRepeaterNode
  | TBranchNode
  | TBranchGroupNode
  | TNumberDataNode
  | TStringDataNode
  | TBoolDataNode
  | TEnumDataNode
  | TStreamDataNode
  | TUnionDataNode
  | TStructDataNode
  | TListDataNode
  | TMapDataNode
  | TListElemNode
  | TListSliceNode
  | TListFillNode
  | TListIndexValueNode
  | TListFindNode
  | TMapKeyValueNode
  | TMapSliceNode
  | TMapFindNode
  | TUnaryOpNode
  | TNAryUnorderedOpNode
  | TNAryOrderedOpNode
  | TFunctionNode
  | TRepeaterInputNode
  | TStateNode
  | TSignalNode
  | TSignalWithDataNode
  | TSignalMergerNode
  | TSignalSplitterNode
  | TSignalForeachNode
  | TNetworkNode
  | TNetworkSocketNode;

export type TNodeType = TNodeUnion["type"];

export type TNodeID<NodeType extends TNodeType> = string & {
  _: NodeType;
};
export type TAnyNodeID = { [K in TNodeType]: TNodeID<K> }[TNodeType];

export type TScene = {
  version: bigint;
  nodes: {
    [NodeType in TNodeType as TNodeID<NodeType>]: Extract<
      TNodeUnion,
      { type: NodeType }
    >;
  };
  positions: { [id: TAnyNodeID]: TPoint };
};

export const CHILD_TYPES = {
  app: { surfaces: "set", states: "set", events: "map" },
  surface: {
    nodes: "list",
    states: "set",
    props: "map",
    events: "map",
  },
  div: {
    nodes: "list",
    states: "set",
    props: "map",
    events: "map",
  },
  text: { props: "map" },
  repeater: { nodes: "list", input: "required" },
  branch: { nodes: "list", condition: "required" },
  branch_group: { nodes: "list" },
  union_data: { options: "list" },
  struct_data: { fields: "list" },
  list_data: { ops: "list", rest: "optional" },
  map_data: { ops: "list", rest: "optional" },
  list_fill: { count: "required" },
  list_index_value: { index: "required", value: "required" },
  list_find: {
    condition: "required",
    match_index: "required",
    match_value: "required",
  },
  map_key_value: { key: "required", value: "required" },
  map_find: {
    condition: "required",
    match_key: "required",
    match_value: "required",
  },
  function: { inputs: "list", outputs: "list", body: "set" },
  repeater_input: { value: "required", index: "optional" },
  state: { updaters: "list", output: "required" },
  signal_with_data: { data: "required" },
  signal_merger: { output_data: "required", inputs: "list" },
  signal_splitter: { outputs: "list" },
  signal_foreach: { input: "required", output: "required" },
  network: { request: "optional", response: "optional" },
  network_socket: {
    close: "required",
    send: "required",
    on_receive: "required",
  },
} as const;

export const NUM_REQUIRED_DESCENDANTS = {
  surface: 1,
};

function createSurface(
  scene: TScene,
  newIds: TNodeDescendantIDList<"surface">
): TScene {
  return upd(scene, "nodes", (v) =>
    set(v, newIds[0], {
      id: newIds[0],
      type: "surface",
    } as TSurfaceNode)
  );
}

export function createNode(
  scene: TScene,
  nodeType: TNodeType,
  newIds: TNodeDescendantIDList<NodeType>
): TScene {
  return {
    surface: () => createSurface(scene, newIds),
  }[nodeType]();
}
